import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { flightService } from '../../services/flightService';
import bookingService from '../../services/bookingService';
import UniversalConfirmModal from '../../components/UniversalConfirmModal/UniversalConfirmModal';
import { mergeBookingData, navigateWithBookingData } from '../../utils/bookingQueryParams';
import apiClient from '../../services/apiClient';

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return '/assets/images/no_aircraft.png';

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  return `${baseUrl}/${imageUrl}`;
};

const PaymentPage = ({ onBack }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  // Merge data from URL params, location state, and session storage
  const bookingData = mergeBookingData(location);

  const [flightData, setFlightData] = useState(null);
  const [returnFlightData, setReturnFlightData] = useState(null);
  const [multiCityFlights, setMultiCityFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [originalBooking, setOriginalBooking] = useState(null);
  const [bookingToken, setBookingToken] = useState(bookingData.booking_token || null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showTimer, setShowTimer] = useState(false);
  const timerIntervalRef = useRef(null);
  const passengers = bookingData.passengerData || [];
  const totalPassengers = bookingData.passengers || 1;
  const routeType = bookingData.routeType || 'One Way';
  const returnFlightId = bookingData.return_flight_id;
  const isReturnFlight = routeType === 'Returns' && returnFlightId;
  const isMultiCity = routeType === 'Multicity' && bookingData.selectedFlights && bookingData.selectedFlights.length > 1;
  const pricing = bookingData.pricing || {
    farePerPax: bookingData.farePerPax,
    totalFare: bookingData.totalFare,
    blockFare: bookingData.blockFare
  };

  console.log('🎫 PaymentPage - Booking Data:', {
    routeType,
    returnFlightId,
    isReturnFlight,
    outboundFlightId: id,
    totalPassengers,
    pricing,
    booking_token: bookingData.booking_token,
    saveForFuture: bookingData.saveForFuture,
    hasPassengerData: !!bookingData.passengerData,
    passengerCount: bookingData.passengerData?.length,
    passengersArray: passengers,
    passengersLength: passengers.length,
    willRedirect: !passengers || passengers.length === 0,
    hasSelectedFlights: !!bookingData.selectedFlights,
    selectedFlightsData: bookingData.selectedFlights
  });

  // Initialize address from booking data or user profile
  useEffect(() => {
    setAddress(bookingData.address || user?.address || '');
  }, [bookingData.address, user?.address]);

  // Timer effect for payment
  useEffect(() => {
    if (showTimer && !timeRemaining) {
      // Start 15-minute timer (600 seconds)
      setTimeRemaining(600);
    }

    if (timeRemaining !== null && timeRemaining > 0 && showTimer) {
      timerIntervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            // Timer expired - show error and reset
            toast.error('Payment session expired. Please try again.');
            setShowTimer(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [showTimer, timeRemaining]);

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (!timeRemaining) return '#000';
    if (timeRemaining <= 60) return '#dc3545'; // Red if less than 1 minute
    if (timeRemaining <= 300) return '#ff9800'; // Orange if less than 5 minutes
    return '#000'; // Black otherwise
  };

  useEffect(() => {
    console.log('🔍 PaymentPage useEffect - Checking passengers:', {
      passengers,
      passengersLength: passengers?.length,
      hasPassengers: !!passengers,
      isEmpty: passengers.length === 0,
      willRedirect: !passengers || passengers.length === 0
    });

    if (!passengers || passengers.length === 0) {
      console.log('❌ No passengers found, redirecting to add-passengers page');
      navigateWithBookingData(navigate, `/add-passengers/${id}`, bookingData);
      return;
    }

    console.log('✅ Passengers found, fetching flight details');
    fetchFlightDetails();

    const loadRebookingData = async () => {
      let currentChangeFlight = user?.change_flight;

      // If we are logged in but don't see change_flight in Redux, it might be stale
      if (user && !currentChangeFlight) {
        try {
          console.log('🔄 [PaymentPage] change_flight missing in Redux, fetching fresh profile...');
          const profileRes = await apiClient.get('/auth/profile');
          if (profileRes.success && profileRes.data.change_flight) {
            currentChangeFlight = profileRes.data.change_flight;
            console.log('✅ [PaymentPage] Found fresh change_flight token:', currentChangeFlight);
          }
        } catch (err) {
          console.warn('⚠️ [PaymentPage] Failed to fetch fresh profile:', err);
        }
      }

      const rebookingToken = bookingData.booking_token || currentChangeFlight;
      if (rebookingToken) {
        console.log('🔄 [PaymentPage] Attempting to fetch original booking with token:', rebookingToken);
        fetchOriginalBooking(rebookingToken);
      }
    };

    loadRebookingData();
  }, [id, user?.id]);

  const fetchOriginalBooking = async (token) => {
    const searchToken = token || user?.change_flight;
    if (!searchToken) return;

    try {
      console.log('🔍 [PaymentPage] Fetching original booking details for:', searchToken);
      const response = await bookingService.getBookingByReference(searchToken);
      if (response.success) {
        console.log('✅ [PaymentPage] Original booking loaded:', response.data.reference_number);
        setOriginalBooking(response.data);
      } else {
        console.warn('⚠️ [PaymentPage] Failed to load original booking:', response.message);
      }
    } catch (err) {
      console.error('❌ [PaymentPage] Error loading original booking:', err);
    }
  };

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);

      console.log('🔍 Fetching flight details:', {
        isReturnFlight,
        outboundId: id,
        returnFlightId,
        routeType,
        hasSelectedFlights: !!bookingData.selectedFlights,
        selectedFlightsCount: bookingData.selectedFlights?.length,
        firstFlightHasImages: bookingData.selectedFlights?.[0]?.aircraft_images,
        firstFlightImageCount: bookingData.selectedFlights?.[0]?.aircraft_images?.length
      });

      // Check if we have selectedFlights data (for multi-city or return flights)
      const hasSelectedFlightsData = bookingData.selectedFlights &&
        Array.isArray(bookingData.selectedFlights) &&
        bookingData.selectedFlights.length > 0;

      if (hasSelectedFlightsData) {
        // Use data from bookingData.selectedFlights (no API call needed)
        console.log('✅ Using selectedFlights data from bookingData (no API call)');

        if (routeType === 'Multicity') {
          // Multi-city: set all flights
          setMultiCityFlights(bookingData.selectedFlights);
          setFlightData(bookingData.selectedFlights[0]); // Set first flight for compatibility
        } else {
          // Return flight: set outbound and return
          setFlightData(bookingData.selectedFlights[0]);
          if (bookingData.selectedFlights.length > 1) {
            setReturnFlightData(bookingData.selectedFlights[1]);
          }
        }
        setLoading(false);
        return;
      }

      // Check if this is a return flight booking
      if (isReturnFlight) {
        // Fetch both flights in a single API call
        console.log('🔍 Calling getMultipleFlightDetails API...');
        const response = await flightService.getMultipleFlightDetails({
          outbound_flight_id: parseInt(id),
          return_flight_id: parseInt(returnFlightId)
        });

        console.log('✅ Multiple flight details response:', response);

        if (response.success) {
          setFlightData(response.data.outbound);
          setReturnFlightData(response.data.return);
          console.log('✅ Set flight data:', {
            outbound: response.data.outbound,
            return: response.data.return
          });
        } else {
          setError('Flights not found');
        }
      } else {
        // Single flight (one-way)
        console.log('🔍 Calling getFlightDetails API for one-way flight...');
        const response = await flightService.getFlightDetails(id);

        console.log('✅ Single flight details response:', response);

        if (response.success) {
          setFlightData(response.data);
          setReturnFlightData(null);
        } else {
          setError('Flight not found');
        }
      }
    } catch (err) {
      console.error('❌ Failed to load flight details:', err);
      setError('Failed to load flight details');
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = () => {
    if (!flightData) return { farePerPax: 0, totalFare: 0, blockFare: 0, outboundFare: 0, returnFare: 0 };

    // Multi-city: sum all flights
    if (isMultiCity && multiCityFlights && multiCityFlights.length > 0) {
      const grandTotal = multiCityFlights.reduce((sum, flight) => {
        const farePax = flight.seat_price
          ? Math.round((parseFloat(flight.seat_price) + (parseFloat(flight.seat_price) * (flight.commission_fee || 0) / 100)) * 100) / 100
          : parseFloat(flight.block_empty_price || 0);
        return sum + farePax * totalPassengers;
      }, 0);
      return { farePerPax: 0, totalFare: grandTotal, blockFare: 0, outboundFare: 0, returnFare: 0, isMultiCity: true };
    }

    console.log('💰 Calculating pricing:', {
      isReturnFlight,
      hasReturnFlightData: !!returnFlightData,
      outboundSeatPrice: flightData.seat_price,
      outboundBlockPrice: flightData.block_empty_price,
      outboundCommission: flightData.commission_fee,
      returnSeatPrice: returnFlightData?.seat_price,
      returnBlockPrice: returnFlightData?.block_empty_price,
      returnCommission: returnFlightData?.commission_fee,
      totalPassengers
    });

    // For return flights, always recalculate from fetched data
    // For one-way flights, use pricing from props if available
    if (!isReturnFlight && pricing && (pricing.farePerPax > 0 || pricing.totalFare > 0 || pricing.blockFare > 0)) {
      console.log('💰 Using pricing from URL params:', pricing);
      return { ...pricing, outboundFare: 0, returnFare: 0 };
    }

    let farePerPax = 0;
    let totalFare = 0;
    let blockFare = 0;
    let outboundFare = 0;
    let returnFare = 0;

    // Calculate outbound flight fare
    if (flightData.seat_price) {
      let outboundPerPax = parseFloat(flightData.seat_price);

      if (flightData.commission_fee) {
        outboundPerPax = outboundPerPax + (outboundPerPax * flightData.commission_fee / 100);
      }

      outboundPerPax = Math.round(outboundPerPax * 100) / 100;
      outboundFare = outboundPerPax * totalPassengers;
      farePerPax = outboundPerPax;
      totalFare = outboundFare;
      console.log('💰 Outbound fare (seat pricing):', { outboundPerPax, outboundFare });
    } else if (flightData.block_empty_price) {
      blockFare = parseFloat(flightData.block_empty_price || 0);

      if (flightData.commission_fee) {
        blockFare = blockFare + (blockFare * flightData.commission_fee / 100);
      }

      blockFare = Math.round(blockFare * 100) / 100;
      outboundFare = blockFare;
      console.log('💰 Outbound fare (block pricing):', { blockFare, outboundFare });
    }

    // Calculate return flight fare if exists
    if (returnFlightData) {
      if (returnFlightData.seat_price) {
        let returnPerPax = parseFloat(returnFlightData.seat_price);

        if (returnFlightData.commission_fee) {
          returnPerPax = returnPerPax + (returnPerPax * returnFlightData.commission_fee / 100);
        }

        returnPerPax = Math.round(returnPerPax * 100) / 100;
        returnFare = returnPerPax * totalPassengers;
        farePerPax = farePerPax + returnPerPax;
        totalFare = outboundFare + returnFare;
        console.log('💰 Return fare (seat pricing):', { returnPerPax, returnFare, totalFare });
      } else if (returnFlightData.block_empty_price) {
        let returnBlockFare = parseFloat(returnFlightData.block_empty_price || 0);

        if (returnFlightData.commission_fee) {
          returnBlockFare = returnBlockFare + (returnBlockFare * returnFlightData.commission_fee / 100);
        }

        returnBlockFare = Math.round(returnBlockFare * 100) / 100;
        returnFare = returnBlockFare;
        blockFare = blockFare + returnBlockFare;
        totalFare = blockFare;
        console.log('💰 Return fare (block pricing):', { returnBlockFare, returnFare, totalFare });
      }
    }

    const result = { farePerPax, totalFare, blockFare, outboundFare, returnFare };

    if (originalBooking) {
      const originalPaid = parseFloat(originalBooking.amount || 0);
      result.originalPaid = originalPaid;
      result.payableDifference = Math.max(0, totalFare - originalPaid);
    }

    console.log('💰 Final pricing calculation:', result);
    return result;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDateTime = (datetime, format = 'time') => {
    const date = new Date(datetime);

    if (format === 'time') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (format === 'date') {
      return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' });
    }
  };

  const calculateDuration = () => {
    if (!flightData) return { hours: 0, minutes: 0 };

    const departure = new Date(flightData.departure.datetime);
    const arrival = new Date(flightData.arrival.datetime);
    const diffMs = arrival - departure;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return { hours, minutes };
  };

  const handleAddressChange = (e) => {
    setAddress(e.target.value);
    // Clear error when user starts typing
    if (addressError) {
      setAddressError('');
    }
  };

  const handleGoBack = () => {
    // Revoke seat lock before going back if token exists
    const currentToken = bookingToken || bookingData.booking_token;
    if (currentToken) {
      console.log(`🔓 [Frontend] handleGoBack: Revoking seat lock for token ${currentToken}`);
      bookingService.revokeLock(currentToken)
        .then(() => console.log('✅ [Frontend] Seat lock revoked successfully'))
        .catch(err => {
          console.warn('⚠️ [Frontend] Failed to revoke lock:', err);
        });
    } else {
      console.log('ℹ️ [Frontend] handleGoBack: No booking_token to revoke');
    }

    // If inside a flow (onBack provided), call it
    if (onBack && typeof onBack === 'function') {
      onBack();
      return;
    }

    // Otherwise, standard navigation back to passengers page
    navigateWithBookingData(
      navigate,
      `/add-passengers/${id}`,
      {
        ...bookingData,
        address
      }
    );
  };

  const handleConfirmRebooking = async () => {
    try {
      setProcessing(true);
      const response = await bookingService.confirmFreeRebooking(user.change_flight);

      if (response.success) {
        toast.success('Flight changed successfully!');
        // Small delay to ensure DB sync
        setTimeout(() => {
          navigate(`/bookings/confirmation/${response.data.bookingToken || user.change_flight}?p=s`);
        }, 1500);
      } else {
        toast.error(response.message || 'Failed to confirm flight change');
      }
    } catch (err) {
      console.error('❌ Rebooking error:', err);
      toast.error('An error occurred while changing your flight.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayNow = () => {
    // Validate address
    if (!address || address.trim() === '') {
      setAddressError('Please enter your address');
      // Scroll to the address field
      const addressElement = document.getElementById('address');
      if (addressElement) {
        addressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        addressElement.focus();
      }
      return;
    }

    // Clear any previous error
    setAddressError('');

    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmPayment = () => {
    setShowConfirmModal(false);
    // Start the timer after user confirms
    setShowTimer(true);
    setTimeRemaining(600);
    setShowPaymentModal(true);
  };

  const handlePaymentMethodSelect = async (method) => {
    console.log('🔘 [Frontend] handlePaymentMethodSelect called with method:', method);
    setShowPaymentModal(false);
    setProcessing(true);

    try {
      // --- NEW: LOCK SEATS HERE ---
      const pricingData = calculatePricing();
      let totalAmount = pricingData.blockFare > 0 ? pricingData.blockFare : pricingData.totalFare;

      // Use payable difference if it's a rebooking
      if (originalBooking) {
        totalAmount = pricingData.payableDifference;

        // Handle zero-delta rebooking (Free)
        if (totalAmount === 0) {
          console.log('✅ [Frontend] Zero-delta rebooking detected. Confirming directly...');
          try {
            setProcessing(true);
            const response = await bookingService.confirmFreeRebooking(bookingToken || user.change_flight);
            if (response.success) {
              toast.success('Flight changed successfully!');
              navigate(`/bookings/confirmation/${response.data.bookingToken || bookingToken}`);
            } else {
              toast.error(response.message || 'Failed to confirm flight change.');
            }
          } catch (err) {
            console.error('❌ Free rebooking error:', err);
            toast.error('An error occurred during flight change.');
          } finally {
            setProcessing(false);
          }
          return;
        }
      }

      if (!totalAmount || totalAmount <= 0 || isNaN(totalAmount)) {
        console.error('❌ Invalid amount:', { totalAmount, pricingData, flightData });
        alert('Invalid booking amount. Please refresh and try again.');
        setProcessing(false);
        return;
      }

      // CHECK FOR DUPLICATE BOOKINGS BEFORE PAYMENT
      console.log('🔍 [Frontend] Checking for duplicate bookings...');
      try {
        // Prepare flights array for duplicate check
        let flightsForCheck = [];
        
        if (routeType === 'Multicity' && multiCityFlights && multiCityFlights.length > 0) {
          flightsForCheck = multiCityFlights.map(flight => ({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport,
            arrival_airport: flight.arrival_airport,
            departure_date_time: flight.departure?.datetime || flight.departure_date_time
          }));
        } else if ((routeType === 'Returns' || routeType === 'return') && flightData && returnFlightData) {
          flightsForCheck = [flightData, returnFlightData].map(flight => ({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport,
            arrival_airport: flight.arrival_airport,
            departure_date_time: flight.departure?.datetime || flight.departure_date_time
          }));
        } else if (bookingData.selectedFlights && Array.isArray(bookingData.selectedFlights) && bookingData.selectedFlights.length > 1) {
          flightsForCheck = bookingData.selectedFlights.map(flight => ({
            flight_number: flight.flight_number,
            departure_airport: flight.departure_airport,
            arrival_airport: flight.arrival_airport,
            departure_date_time: flight.departure?.datetime || flight.departure_date_time
          }));
        } else if (flightData) {
          flightsForCheck = [{
            flight_number: flightData.flight_number,
            departure_airport: flightData.departure_airport,
            arrival_airport: flightData.arrival_airport,
            departure_date_time: flightData.departure?.datetime || flightData.departure_date_time
          }];
        }

        // Check for duplicates
        const duplicateCheckResponse = await bookingService.checkDuplicateBooking(passengers, flightsForCheck);
        
        if (!duplicateCheckResponse.success && duplicateCheckResponse.data?.duplicates) {
          console.warn('⚠️ [Frontend] Duplicate booking detected:', duplicateCheckResponse.data.duplicates);
          toast.error(`Cannot proceed: Passenger already booked on this flight.`, { duration: 8000 });
          setProcessing(false);
          return;
        }
        
        console.log('✅ [Frontend] No duplicate bookings found. Proceeding with payment...');
      } catch (err) {
        console.error('❌ [Frontend] Error checking duplicates:', err);
        // Check if error response contains duplicate info
        if (err.data?.duplicates) {
          console.warn('⚠️ [Frontend] Duplicate booking detected in error response:', err.data.duplicates);
          toast.error(`Cannot proceed: Passenger already booked on this flight.`, { duration: 8000 });
          setProcessing(false);
          return;
        }
        // Don't block payment if duplicate check fails for other reasons - log and continue
        console.warn('⚠️ [Frontend] Continuing despite duplicate check error');
      }

      // 1. Identify all flight legs
      let aircraftData = [];
      const currentRouteType = routeType === 'Returns' || routeType === 'return' ? 'return' : routeType === 'Multicity' ? 'multi_city' : 'one_way';

      if (currentRouteType === 'multi_city' && multiCityFlights && multiCityFlights.length > 0) {
        aircraftData = multiCityFlights.map(flight => ({
          routes_id: flight.id,
          aircraft_id: flight.aircraft_id,
          registration_number: flight.registration_number,
          flight_number: flight.flight_number,
          departure_airport: flight.departure_airport,
          departure_date_time: flight.departure?.datetime || flight.departure_date_time,
          arrival_airport: flight.arrival_airport,
          arrival_date_time: flight.arrival?.datetime || flight.arrival_date_time,
          seat_price: flight.seat_price || flight.block_empty_price,
        }));
      } else if (currentRouteType === 'return' && flightData && returnFlightData) {
        aircraftData = [flightData, returnFlightData].map(flight => ({
          routes_id: flight.id,
          aircraft_id: flight.aircraft_id,
          registration_number: flight.registration_number,
          flight_number: flight.flight_number,
          departure_airport: flight.departure_airport,
          departure_date_time: flight.departure?.datetime || flight.departure_date_time,
          arrival_airport: flight.arrival_airport,
          arrival_date_time: flight.arrival?.datetime || flight.arrival_date_time,
          seat_price: flight.seat_price || flight.block_empty_price,
        }));
      } else if (bookingData.selectedFlights && Array.isArray(bookingData.selectedFlights) && bookingData.selectedFlights.length > 1) {
        aircraftData = bookingData.selectedFlights.map(flight => ({
          routes_id: flight.id,
          aircraft_id: flight.aircraft_id,
          registration_number: flight.registration_number,
          flight_number: flight.flight_number,
          departure_airport: flight.departure_airport,
          departure_date_time: flight.departure?.datetime || flight.departure_date_time,
          arrival_airport: flight.arrival_airport,
          arrival_date_time: flight.arrival?.datetime || flight.arrival_date_time,
          seat_price: flight.seat_price || flight.block_empty_price,
        }));
      }

      const isMultiLeg = aircraftData.length > 1;
      const flightsToLock = isMultiLeg ? aircraftData : [flightData];
      const route_ids = flightsToLock.map(f => f.routes_id || f.id).filter(Boolean);

      // 2. Lock seats with comprehensive data
      const lockPayload = {
        ...bookingData,
        ...flightData,
        aircraftData: isMultiLeg ? aircraftData : undefined,
        passengers: passengers,
        passenger_count: totalPassengers,
        route_type: currentRouteType,
        amount: totalAmount
      };

      console.log('🔒 [Frontend] Locking seats. IDs:', route_ids, 'Payload:', lockPayload);

      const lockResponse = await bookingService.lockSeats(
        route_ids,
        totalPassengers,
        lockPayload,
        bookingToken
      );

      if (!lockResponse.success) {
        toast.error(lockResponse.message || 'Failed to lock seats. They might have been taken.');
        setProcessing(false);
        return;
      }

      const newToken = lockResponse.data.booking_token;
      setBookingToken(newToken);
      localStorage.setItem('pending_booking_token', newToken);
      localStorage.setItem('pending_booking_time', Date.now().toString());

      // 3. Prepare final payment data
      let paymentBookingData;
      if (isMultiLeg) {
        paymentBookingData = {
          aircraftData: aircraftData,
          selected_seat: totalPassengers,
          amount: totalAmount,
          passengers: passengers,
          address: address,
          route_type: currentRouteType,
          saveForFuture: bookingData.saveForFuture || false,
          flight_number: aircraftData[0].flight_number,
          departure_airport: aircraftData[0].departure_airport,
          departure_date_time: aircraftData[0].departure_date_time,
          arrival_airport: aircraftData[aircraftData.length - 1].arrival_airport,
          arrival_date_time: aircraftData[aircraftData.length - 1].arrival_date_time,
        };
      } else {
        paymentBookingData = {
          flight_id: flightData.id,
          aircraft_id: flightData.aircraft_id,
          registration_number: flightData.registration_number,
          flight_number: flightData.flight_number,
          departure_airport: flightData.departure_airport,
          departure_date_time: flightData.departure.datetime || flightData.departure_date_time,
          arrival_airport: flightData.arrival_airport,
          arrival_date_time: flightData.arrival.datetime || flightData.arrival_date_time,
          selected_seat: totalPassengers,
          amount: totalAmount,
          routes_id: flightData.id,
          passengers: passengers,
          address: address,
          route_type: 'one_way',
          saveForFuture: bookingData.saveForFuture || false
        };
      }

      // Create payment session based on selected method
      if (method === 'stripe') {
        const paymentService = (await import('../../services/paymentService')).default;
        const response = await paymentService.createStripeSession(
          paymentBookingData,
          totalAmount,
          newToken // Use the newly acquired token
        );

        if (response.success) {
          // Handle no payment required case (same price rebooking)
          if (response.data.no_payment_required) {
            console.log('✅ No payment required - confirming free rebooking');
            const confirmResponse = await paymentService.confirmFreeRebooking(
              response.data.booking_token
            );
            
            if (confirmResponse.success) {
              toast.success('Booking confirmed successfully!', { duration: 3000 });
              // Redirect to booking confirmation page
              setTimeout(() => {
                navigate(`/jetsetter/bookings/${response.data.booking_token}`);
              }, 1500);
            } else {
              toast.error('Failed to confirm booking', { duration: 3000 });
              setProcessing(false);
            }
          } else if (response.data.url) {
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
          } else {
            alert('Failed to create payment session: Invalid response');
            setProcessing(false);
          }
        } else {
          alert('Failed to create payment session: ' + (response.message || 'Unknown error'));
          setProcessing(false);
        }
      } else if (method === 'paypal') {
        const paymentService = (await import('../../services/paymentService')).default;
        const response = await paymentService.createPayPalPayment(
          paymentBookingData,
          totalAmount,
          newToken // Use the newly acquired token
        );

        if (response.success) {
          // Handle no payment required case (same price rebooking)
          if (response.data.no_payment_required) {
            console.log('✅ No payment required - confirming free rebooking');
            const confirmResponse = await paymentService.confirmFreeRebooking(
              response.data.booking_token
            );
            
            if (confirmResponse.success) {
              toast.success('Booking confirmed successfully!', { duration: 3000 });
              // Redirect to booking confirmation page
              setTimeout(() => {
                navigate(`/jetsetter/bookings/${response.data.booking_token}`);
              }, 1500);
            } else {
              toast.error('Failed to confirm booking', { duration: 3000 });
              setProcessing(false);
            }
          } else if (response.data.approval_url) {
            // Redirect to PayPal checkout
            window.location.href = response.data.approval_url;
          } else {
            alert('Failed to create PayPal payment: Invalid response');
            setProcessing(false);
          }
        } else {
          alert('Failed to create PayPal payment: ' + (response.message || 'Unknown error'));
          setProcessing(false);
        }
      } else {
        alert('Invalid payment method selected');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      // Use err.data?.message or err.message from our custom ApiClient
      const errorMsg = err.data?.message || err.message || 'Failed to process payment. Please try again.';
      toast.error(errorMsg, { duration: 6000 });
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-jet-gray-100 pt-4 pb-4 flex items-center justify-center">
        <img src="/assets/images/flight_logo.gif" alt="Loading..." className="w-24 h-24" />
      </div>
    );
  }

  if (error || !flightData) {
    return (
      <div className="min-h-screen bg-jet-gray-100 pt-4 pb-4">
        <div className="w-[86%] mx-auto py-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Flight not found'}
          </div>
        </div>
      </div>
    );
  }

  const pricingData = calculatePricing();
  const duration = calculateDuration();
  const totalAmount = pricingData.blockFare > 0 ? pricingData.blockFare : pricingData.totalFare;

  return (
    <div className="min-h-screen bg-jet-gray-100 pt-4 pb-4">
      {/* Main Content */}
      <div className="w-[86%] mx-auto py-8">
        {/* Outer White Wrapper */}
        <div className="bg-white rounded-lg shadow-jet p-6">
          <div className="space-y-6">
            {/* Combined Card - Flight Details, Fare Summary, Contact Details */}
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
              {/* Flight Details Section */}
              {isMultiCity && multiCityFlights.length > 0 ? (
                // Multi-City Flight Display - Show all flights
                <div className="p-6 space-y-6">
                  {multiCityFlights.map((flight, index) => (
                    <div key={flight.id} className={index < multiCityFlights.length - 1 ? "pb-6 border-b-2 border-dashed border-gray-300" : ""}>
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Aircraft Image */}
                        <div className="lg:col-span-2">
                          <div className="rounded-lg overflow-hidden h-32">
                            {flight.aircraft_images && flight.aircraft_images.length > 0 && !imageErrors[`multi-${flight.id}`] ? (
                              <img
                                src={getImageUrl(flight.aircraft_images[0].presigned_url || flight.aircraft_images[0].url)}
                                alt={flight.aircraft_images[0].name || 'Aircraft'}
                                className="w-full h-full object-cover"
                                onError={() => {
                                  setImageErrors(prev => ({ ...prev, [`multi-${flight.id}`]: true }));
                                }}
                              />
                            ) : (
                              <img
                                src="/assets/images/no_aircraft.png"
                                alt="Aircraft"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null; // Prevent infinite loop
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* Flight Details */}
                        <div className="lg:col-span-10">
                          {/* Flight Number */}
                          <div className="mb-4">
                            <h4 className="text-base font-bold text-jet-text">
                              {flight.flight_number?.toUpperCase()}
                            </h4>
                          </div>

                          {/* Departure, Duration, Arrival */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Departure */}
                            <div>
                              <h4 className="text-sm font-bold text-jet-text mb-1">
                                {flight.departure.airport_code} {formatDateTime(flight.departure.datetime, 'time')}
                              </h4>
                              <p className="text-xs text-jet-text-light mb-2">
                                {formatDateTime(flight.departure.datetime, 'date')}
                              </p>
                            </div>

                            {/* Duration */}
                            <div className="text-center">
                              <p className="text-2xl text-jet-primary mb-1">→</p>
                              <p className="text-xs text-jet-text-light">
                                {Math.floor(flight.duration_minutes / 60)} Hrs {flight.duration_minutes % 60} Mins
                              </p>
                            </div>

                            {/* Arrival */}
                            <div className="text-right">
                              <h4 className="text-sm font-bold text-jet-text mb-1">
                                {flight.arrival.airport_code} {formatDateTime(flight.arrival.datetime, 'time')}
                              </h4>
                              <p className="text-xs text-jet-text-light">
                                {formatDateTime(flight.arrival.datetime, 'date')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : isReturnFlight && returnFlightData ? (
                // Return Flight Display - Show both flights
                <div className="p-6 space-y-6">
                  {/* Outbound Flight */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-6 border-b-2 border-dashed border-gray-300">
                    {/* Aircraft Image */}
                    <div className="lg:col-span-2">
                      <div className="rounded-lg overflow-hidden h-32">
                        {flightData.aircraft_images && flightData.aircraft_images.length > 0 && !imageErrors['outbound'] ? (
                          <img
                            src={getImageUrl(flightData.aircraft_images[0].presigned_url || flightData.aircraft_images[0].url)}
                            alt={flightData.aircraft_images[0].name || 'Aircraft'}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setImageErrors(prev => ({ ...prev, outbound: true }));
                            }}
                          />
                        ) : (
                          <img
                            src="/assets/images/no_aircraft.png"
                            alt="Aircraft"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div className="lg:col-span-10">
                      {/* Flight Number */}
                      <div className="mb-4">
                        <h4 className="text-base font-bold text-jet-text">
                          {flightData.flight_number?.toUpperCase()}
                        </h4>
                      </div>

                      {/* Departure, Duration, Arrival */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Departure */}
                        <div>
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.departure.airport_code} {formatDateTime(flightData.departure.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light mb-2">
                            {formatDateTime(flightData.departure.datetime, 'date')}
                          </p>
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                          <p className="text-2xl text-jet-primary mb-1">→</p>
                          <p className="text-xs text-jet-text-light">
                            {Math.floor(flightData.duration_minutes / 60)} Hrs {flightData.duration_minutes % 60} Mins
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.arrival.airport_code} {formatDateTime(flightData.arrival.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light">
                            {formatDateTime(flightData.arrival.datetime, 'date')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Return Flight */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Aircraft Image */}
                    <div className="lg:col-span-2">
                      <div className="rounded-lg overflow-hidden h-32">
                        {returnFlightData.aircraft_images && returnFlightData.aircraft_images.length > 0 && !imageErrors['return'] ? (
                          <img
                            src={getImageUrl(returnFlightData.aircraft_images[0].presigned_url || returnFlightData.aircraft_images[0].url)}
                            alt={returnFlightData.aircraft_images[0].name || 'Aircraft'}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setImageErrors(prev => ({ ...prev, return: true }));
                            }}
                          />
                        ) : (
                          <img
                            src="/assets/images/no_aircraft.png"
                            alt="Aircraft"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div className="lg:col-span-10">
                      {/* Flight Number */}
                      <div className="mb-4">
                        <h4 className="text-base font-bold text-jet-text">
                          {returnFlightData.flight_number?.toUpperCase()}
                        </h4>
                      </div>

                      {/* Departure, Duration, Arrival */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Departure */}
                        <div>
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {returnFlightData.departure.airport_code} {formatDateTime(returnFlightData.departure.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light mb-2">
                            {formatDateTime(returnFlightData.departure.datetime, 'date')}
                          </p>
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                          <p className="text-2xl text-jet-primary mb-1">→</p>
                          <p className="text-xs text-jet-text-light">
                            {Math.floor(returnFlightData.duration_minutes / 60)} Hrs {returnFlightData.duration_minutes % 60} Mins
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {returnFlightData.arrival.airport_code} {formatDateTime(returnFlightData.arrival.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light">
                            {formatDateTime(returnFlightData.arrival.datetime, 'date')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // One-Way Flight Display (original)
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Aircraft Image */}
                    <div className="lg:col-span-2">
                      <div className="rounded-lg overflow-hidden h-32">
                        {flightData.aircraft_images && flightData.aircraft_images.length > 0 && !imageErrors['oneway'] ? (
                          <img
                            src={getImageUrl(flightData.aircraft_images[0].presigned_url || flightData.aircraft_images[0].url)}
                            alt={flightData.aircraft_images[0].name || 'Aircraft'}
                            className="w-full h-full object-cover"
                            onError={() => {
                              setImageErrors(prev => ({ ...prev, oneway: true }));
                            }}
                          />
                        ) : (
                          <img
                            src="/assets/images/no_aircraft.png"
                            alt="Aircraft"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null; // Prevent infinite loop
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Flight Details */}
                    <div className="lg:col-span-10">
                      {/* Flight Number */}
                      <div className="mb-4">
                        <h4 className="text-base font-bold text-jet-text">
                          {flightData.flight_number?.toUpperCase()}
                        </h4>
                      </div>

                      {/* Departure, Duration, Arrival */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Departure */}
                        <div>
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.departure.airport_code} {formatDateTime(flightData.departure.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light mb-2">
                            {formatDateTime(flightData.departure.datetime, 'date')}
                          </p>
                          {pricingData.blockFare > 0 ? (
                            <p className="text-xs font-bold text-jet-text">
                              Total Fare ${formatCurrency(pricingData.blockFare)}
                            </p>
                          ) : (
                            <div className="flex gap-4">
                              <p className="text-xs font-bold text-jet-text">
                                Fare/Pax ${formatCurrency(pricingData.farePerPax)}
                              </p>
                              <p className="text-xs font-bold text-jet-text">
                                Total Fare ${formatCurrency(pricingData.totalFare)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                          <p className="text-2xl text-jet-primary mb-1">→</p>
                          <p className="text-xs text-jet-text-light">
                            {duration.hours} Hrs {duration.minutes} Mins
                          </p>
                        </div>

                        {/* Arrival */}
                        <div className="text-right">
                          <h4 className="text-sm font-bold text-jet-text mb-1">
                            {flightData.arrival.airport_code} {formatDateTime(flightData.arrival.datetime, 'time')}
                          </h4>
                          <p className="text-xs text-jet-text-light">
                            {formatDateTime(flightData.arrival.datetime, 'date')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fare Summary and Contact Details Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 p-[10px]">
                {/* Fare Summary */}
                <div className="p-[5px]">
                  <div className="bg-jet-yellow px-6 py-3 rounded-[5px]">
                    <h5 className="text-base font-bold text-jet-text">Fare Summary</h5>
                  </div>
                  <div className="p-6">
                    {isMultiCity && multiCityFlights && multiCityFlights.length > 0 ? (
                      // Multi-city fare breakdown
                      <div className="space-y-3">
                        {multiCityFlights.map((flight, idx) => {
                          const farePax = flight.seat_price
                            ? Math.round((parseFloat(flight.seat_price) + (parseFloat(flight.seat_price) * (flight.commission_fee || 0) / 100)) * 100) / 100
                            : parseFloat(flight.block_empty_price || 0);
                          return (
                            <div key={idx} className="pb-3 border-b-2 border-dashed border-gray-300">
                              <p className="text-xs font-bold text-jet-text mb-2">
                                {flight.departure?.airport_code || 'DEP'} → {flight.arrival?.airport_code || 'ARR'}
                              </p>
                              <div className="flex justify-between items-center mb-1">
                                <p className="text-xs text-jet-text">Passenger(s)</p>
                                <p className="text-xs text-jet-text">{totalPassengers}</p>
                              </div>
                              <div className="flex justify-between items-center mb-1">
                                <p className="text-xs text-jet-text">Fare/Pax</p>
                                <p className="text-xs text-jet-text">$ {formatCurrency(farePax)}</p>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className="text-xs font-bold text-jet-text">Total Fare</p>
                                <p className="text-xs font-bold text-jet-text">$ {formatCurrency(farePax * totalPassengers)}</p>
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-2">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-jet-text">Total Passenger(s)</p>
                            <b className="text-sm text-jet-text">{totalPassengers}</b>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm font-bold text-jet-text">Grand Total</p>
                            <b className="text-base text-jet-text">$ {formatCurrency(totalAmount)}</b>
                          </div>
                        </div>
                      </div>
                    ) : isReturnFlight && returnFlightData ? (
                      // Return flight fare breakdown
                      <div className="space-y-3">
                        {/* Outbound Flight */}
                        <div className="pb-3 border-b-2 border-dashed border-gray-300">
                          <p className="text-xs font-bold text-jet-text mb-2">
                            {flightData.departure.airport_code} → {flightData.arrival.airport_code}
                          </p>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Passengers</p>
                            <p className="text-xs text-jet-text">{totalPassengers}</p>
                          </div>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Fare/Pax</p>
                            <p className="text-xs text-jet-text">
                              $ {formatCurrency(pricingData.outboundFare / totalPassengers)}
                            </p>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-jet-text">Total Fare</p>
                            <p className="text-xs font-bold text-jet-text">
                              $ {formatCurrency(pricingData.outboundFare)}
                            </p>
                          </div>
                        </div>

                        {/* Return Flight */}
                        <div className="pb-3 border-b-2 border-dashed border-gray-300">
                          <p className="text-xs font-bold text-jet-text mb-2">
                            {returnFlightData.departure.airport_code} → {returnFlightData.arrival.airport_code}
                          </p>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Passengers</p>
                            <p className="text-xs text-jet-text">{totalPassengers}</p>
                          </div>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-xs text-jet-text">Fare/Pax</p>
                            <p className="text-xs text-jet-text">
                              $ {formatCurrency(pricingData.returnFare / totalPassengers)}
                            </p>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-jet-text">Total Fare</p>
                            <p className="text-xs font-bold text-jet-text">
                              $ {formatCurrency(pricingData.returnFare)}
                            </p>
                          </div>
                        </div>

                        {/* Grand Total */}
                        <div className="pt-2">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-jet-text">Total Passenger(s)</p>
                            <b className="text-sm text-jet-text">{totalPassengers}</b>
                          </div>
                          {originalBooking ? (
                            <>
                              <div className="flex justify-between items-center border-t border-gray-200 pt-2 pb-2">
                                <p className="text-sm text-jet-text">New Flight Fare</p>
                                <b className="text-sm text-jet-text">$ {formatCurrency(totalAmount)}</b>
                              </div>
                              <div className="flex justify-between items-center border-b border-gray-200 pb-2 text-green-600">
                                <p className="text-sm">Old Payment Done</p>
                                <b className="text-sm">- $ {formatCurrency(pricingData.originalPaid || 0)}</b>
                              </div>
                              <div className="flex justify-between items-center pt-2">
                                <p className="text-sm font-bold text-jet-text">Total Fare to Pay</p>
                                <b className="text-base text-jet-primary">$ {formatCurrency(pricingData.payableDifference || 0)}</b>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between items-center">
                              <p className="text-sm font-bold text-jet-text">Total Fare</p>
                              <b className="text-base text-jet-text">$ {formatCurrency(totalAmount)}</b>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // One-way flight fare (original)
                      <div className="space-y-3">
                        {originalBooking ? (
                          <>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                              <p className="text-sm text-jet-text">New Flight Fare</p>
                              <b className="text-sm text-jet-text">$ {formatCurrency(totalAmount)}</b>
                            </div>
                            <div className="flex justify-between items-center border-b border-gray-200 pb-2 text-green-600">
                              <p className="text-sm">Original Booking Credit</p>
                              <b className="text-sm">- $ {formatCurrency(pricingData.originalPaid || 0)}</b>
                            </div>
                            <div className="flex justify-between items-center pt-2">
                              <p className="text-sm font-bold text-jet-text">Amount to Pay</p>
                              <b className="text-base text-jet-primary">$ {formatCurrency(pricingData.payableDifference || 0)}</b>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between items-center border-b-2 border-dotted border-gray-200">
                              <p className="text-sm text-jet-text">Total Passenger(s)</p>
                              <b className="text-sm text-jet-text">{totalPassengers}</b>
                            </div>
                            <div className="flex justify-between items-center pt-2">
                              <p className="text-sm text-jet-text">Total Fare</p>
                              <b className="text-base text-jet-text">$ {formatCurrency(totalAmount)}</b>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="p-[5px]">
                  <div className="bg-jet-yellow px-6 py-3 rounded-[5px]">
                    <h5 className="text-base font-bold text-jet-text">Contact Details</h5>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start border-b-2 border-dotted border-gray-200">
                        <p className="text-sm text-jet-text">Name</p>
                        <p className="text-sm font-semibold text-jet-text uppercase text-right">
                          {user?.first_name} {user?.last_name}
                        </p>
                      </div>
                      <div className="flex justify-between items-start border-b-2 border-dotted border-gray-200">
                        <p className="text-sm text-jet-text">Email</p>
                        <p className="text-sm font-semibold text-jet-text uppercase text-right">
                          {user?.email}
                        </p>
                      </div>
                      <div className="flex justify-between items-start border-b-2 border-dotted border-gray-200">
                        <p className="text-sm text-jet-text">Phone Number :</p>
                        <p className="text-sm font-semibold text-jet-text text-right">
                          {passengers[0]?.country_code || user?.phone_code} {passengers[0]?.contact_number || user?.mobile_number}
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-jet-text">Address :</p>
                        {user?.address ? (
                          <p className="text-sm font-semibold text-jet-text uppercase text-right flex-1 ml-4">
                            {user.address}
                          </p>
                        ) : (
                          <div className="flex-1 ml-4">
                            <textarea
                              id="address"
                              name="address"
                              className={`w-full px-3 py-2 border rounded text-sm uppercase focus:outline-none focus:ring-2 resize-none ${addressError
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-jet-border focus:ring-jet-primary'
                                }`}
                              value={address}
                              onChange={handleAddressChange}
                              placeholder="Enter Your Address..."
                              rows="2"
                            />
                            {addressError && (
                              <p className="text-red-500 text-xs mt-1">
                                {addressError}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Button */}
              <div className="flex flex-col items-center gap-3 py-6">
                {/* Booking Countdown Timer */}
                {showTimer && timeRemaining !== null && (
                  <div className="w-full px-6">
                    <div style={{
                      padding: '12px',
                      background: '#fffbf0',
                      borderLeft: '4px solid #ffd200',
                      borderRadius: '4px',
                    }}>
                      <div className="flex items-center gap-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffd200" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
                          Complete payment within{' '}
                          <strong style={{
                            color: getTimerColor(),
                            fontSize: '15px',
                            fontFamily: "'Courier New', monospace",
                            fontWeight: '700'
                          }}>
                            {formatTime(timeRemaining)}
                          </strong>
                          {' '}to secure your booking
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-4">
                  <button
                    type="button"
                    className="px-8 py-2 bg-gray-300 text-jet-text font-bold rounded hover:bg-gray-400 transition-colors shadow-md text-sm"
                    onClick={handleGoBack}
                  >
                    Back
                  </button>
                  {user?.change_flight && (pricingData.payableDifference === 0 || !pricingData.payableDifference) ? (
                    <button
                      type="button"
                      className="px-8 py-2 bg-green-600 text-white font-bold rounded hover:bg-green-700 transition-colors shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      onClick={handleConfirmRebooking}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : 'Confirm Change'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="px-8 py-2 bg-jet-yellow text-jet-text font-bold rounded hover:bg-yellow-500 transition-colors shadow-md disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
                      onClick={handlePayNow}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : (originalBooking && pricingData.payableDifference === 0 ? 'Book Now' : 'Pay Now')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <UniversalConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmPayment}
        type="warning"
        title="Are you sure?"
        message="Please check and confirm that all passenger(s) information are entered correctly before proceeding to avoid additional charges. Click Ok to proceed or Cancel to exit."
        confirmText="OK"
        cancelText="CANCEL"
        showCancelButton={true}
        theme="modern"
      />

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-[30rem] w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Yellow Header */}
            <div className="border-t-8 border-yellow-400 px-8 py-4">
              <h5 className="text-2xl font-bold text-gray-800 m-0 text-center">Select Payment Method</h5>
            </div>

            {/* Content */}
            <div className="p-[10px]">
              <div className="grid grid-cols-2 mb-4 gap-0">
                {/* Stripe Option */}
                <div
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => setPaymentMethod('stripe')}
                >
                  <div className="bg-gray-300 rounded-lg p-4 w-[75%] flex flex-col items-center justify-center min-h-40">
                    <div className="mb-4">
                      <input
                        type="radio"
                        name="payment_method"
                        value="stripe"
                        checked={paymentMethod === 'stripe'}
                        onChange={() => setPaymentMethod('stripe')}
                        className="w-6 h-6 cursor-pointer"
                      />
                    </div>
                    <img src="/stripe_logo.png" alt="Stripe" className="max-w-full h-12 object-contain" />
                  </div>
                </div>

                {/* PayPal Option */}
                <div
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => setPaymentMethod('paypal')}
                >
                  <div className="bg-gray-300 rounded-lg w-[75%] flex flex-col items-center justify-center min-h-40">
                    <div className="mb-4">
                      <input
                        type="radio"
                        name="payment_method"
                        value="paypal"
                        checked={paymentMethod === 'paypal'}
                        onChange={() => setPaymentMethod('paypal')}
                        className="w-6 h-6 cursor-pointer"
                      />
                    </div>
                    <img src="/PayPal-Logo.png" alt="PayPal" className="max-w-full h-16 object-contain" />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-center gap-4">
                <button
                  type="button"
                  className="px-12 py-3 bg-yellow-400 text-gray-800 font-bold rounded hover:bg-yellow-500 transition-colors"
                  onClick={() => {
                    if (paymentMethod) {
                      handlePaymentMethodSelect(paymentMethod);
                    }
                  }}
                >
                  OK
                </button>
                <button
                  type="button"
                  className="px-12 py-3 border-2 border-gray-400 text-gray-800 font-bold rounded hover:border-gray-600 transition-colors bg-white"
                  onClick={() => setShowPaymentModal(false)}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
